// utils/canadaSearchSystem.js - Canada-specific search system
import { canadaFeedsConfig, getProvincialInfo, getProvincialFeedsByBranch } from './canadaFeedsConfig.js';
import { canadaDepartmentConfig, getCanadaDepartmentConfig } from './canadaDepartmentConfig.js';

// Provincial information lookup (from your existing config)
const provincialInfo = {
  alberta: { fullName: 'Alberta', code: 'ab', flagImage: '/albertaFlag.png', tableName: 'alberta' },
  british_columbia: { fullName: 'British Columbia', code: 'bc', flagImage: '/britishColumbiaFlag.png', tableName: 'british_columbia' },
  manitoba: { fullName: 'Manitoba', code: 'mb', flagImage: '/manitobaFlag.png', tableName: 'manitoba' },
  new_brunswick: { fullName: 'New Brunswick', code: 'nb', flagImage: '/newBrunswickFlag.png', tableName: 'new_brunswick' },
  newfoundland_labrador: { fullName: 'Newfoundland and Labrador', code: 'nl', flagImage: '/newfoundlandAndLabradorFlag.png', tableName: 'newfoundland_labrador' },
  northwest_territories: { fullName: 'Northwest Territories', code: 'nt', flagImage: '/northwestTerritoriesFlag.png', tableName: 'northwest_territories' },
  nova_scotia: { fullName: 'Nova Scotia', code: 'ns', flagImage: '/novaScotiaFlag.png', tableName: 'nova_scotia' },
  nunavut: { fullName: 'Nunavut', code: 'nu', flagImage: '/nunavutFlag.png', tableName: 'nunavut' },
  ontario: { fullName: 'Ontario', code: 'on', flagImage: '/ontarioFlag.png', tableName: 'ontario' },
  prince_edward_island: { fullName: 'Prince Edward Island', code: 'pe', flagImage: '/princeEdwardIslandFlag.png', tableName: 'prince_edward_island' },
  quebec: { fullName: 'Quebec', code: 'qc', flagImage: '/quebecFlag.png', tableName: 'quebec' },
  saskatchewan: { fullName: 'Saskatchewan', code: 'sk', flagImage: '/saskatchewanFlag.png', tableName: 'saskatchewan' },
  yukon: { fullName: 'Yukon', code: 'yt', flagImage: '/yukonFlag.png', tableName: 'yukon' }
};

// Search relevant pages for Canada - similar to US searchSystem but Canada-specific
export const searchCanadaRelevantPages = (query, limit = 12) => {
  if (!query || !query.trim()) return [];
  
  const normalizedQuery = query.toLowerCase().trim();
  const results = [];
  
  // console.log('=== CANADA SEARCH DEBUG ===');
  // console.log('Query:', normalizedQuery);

  // 1. Search provinces - exact name matching
  Object.entries(provincialInfo).forEach(([provinceKey, provinceData]) => {
    const fullName = provinceData.fullName.toLowerCase();
    
    if (fullName.includes(normalizedQuery) || normalizedQuery.includes(fullName)) {
      // Main province page
      results.push({
        title: `${provinceData.fullName} Page`,
        description: `${provinceData.fullName} provincial government information and updates`,
        route: `/canada/${provinceKey}`,
        icon: 'Map',
        type: 'province',
        priority: 1
      });
      
      // Province feeds
      results.push({
        title: `${provinceData.fullName} Updates`,
        description: `View all updates from ${provinceData.fullName}`,
        route: `/feed/canada/${provinceData.tableName}`,
        icon: 'FileText',
        type: 'feed',
        priority: 2
      });
      
      // console.log('Found province match:', provinceData.fullName);
    }
  });

  // 2. Search departments/agencies
  Object.entries(canadaDepartmentConfig).forEach(([deptKey, deptData]) => {
    if (!deptData.departmentInfo) return;
    
    const deptName = deptData.departmentInfo.name.toLowerCase();
    
    if (deptName.includes(normalizedQuery) || 
        deptName.replace('canada ', '').includes(normalizedQuery) ||
        deptName.replace(' of canada', '').includes(normalizedQuery)) {
      
      // Main department page
      results.push({
        title: deptData.departmentInfo.name,
        description: deptData.departmentInfo.missionStatement || 'Canadian government department information and services',
        route: deptData.departmentInfo.route,
        icon: 'Building',
        type: 'department',
        priority: 1
      });

      // Add department main feeds
      if (deptData.mainFeeds && deptData.mainFeeds.length > 0) {
        deptData.mainFeeds.forEach(feed => {
          if (feed.apiParams) {
            const schemaMatch = feed.apiParams.match(/schema=([^&]+)/);
            const tableMatch = feed.apiParams.match(/table=([^&]+)/);
            
            if (schemaMatch && tableMatch) {
              const schema = decodeURIComponent(schemaMatch[1]);
              const table = decodeURIComponent(tableMatch[1]);
              
              let feedRoute = `/feed/${schema}/${table}`;
              
              const filterMatch = feed.apiParams.match(/filter_column=([^&]+)&filter_string=([^&]+)/);
              if (filterMatch) {
                const filterColumn = decodeURIComponent(filterMatch[1]);
                const filterString = decodeURIComponent(filterMatch[2]);
                feedRoute += `?filter_column=${filterColumn}&filter_string=${encodeURIComponent(filterString)}`;
              }
              
              results.push({
                title: `${deptData.departmentInfo.name} - ${feed.name}`,
                description: `View all ${feed.name.toLowerCase()} from ${deptData.departmentInfo.name}`,
                route: feedRoute,
                icon: 'List',
                type: 'feed',
                priority: 2
              });
            }
          }
        });
      }

      // console.log('Found department match:', deptData.departmentInfo.name);
    }
  });

  // 3. Search sections (legislative, executive, judicial, etc.)
  const sectionMappings = {
    'legislative': { 
      title: 'Legislative Branch', 
      description: 'Parliament, bills, committees, and legislative proceedings'
    },
    'executive': { 
      title: 'Executive Branch', 
      description: 'Prime Minister\'s Office and government agencies'
    },
    'judicial': { 
      title: 'Judicial Branch', 
      description: 'Supreme Court decisions and judicial proceedings'
    },
    'economy': { 
      title: 'Economy Section', 
      description: 'Economic policy, taxation, and financial oversight'
    },
    'environment': { 
      title: 'Environment Section', 
      description: 'Environmental policy, climate action, and conservation'
    },
    'science': { 
      title: 'Science & Technology', 
      description: 'Research, innovation, and technological development'
    },
    'foreign-affairs': { 
      title: 'Foreign Affairs', 
      description: 'International relations and diplomatic affairs'
    }
  };

  Object.entries(sectionMappings).forEach(([sectionKey, sectionInfo]) => {
    const sectionTitle = sectionInfo.title.toLowerCase();
    
    if (sectionKey.includes(normalizedQuery) || 
        sectionTitle.includes(normalizedQuery) ||
        normalizedQuery === sectionKey) {
      
      // Main section page
      results.push({
        title: sectionInfo.title,
        description: sectionInfo.description,
        route: `/canada/${sectionKey}`,
        icon: 'Globe',
        type: 'section',
        priority: 3
      });

      // Add feeds from this section
      if (canadaFeedsConfig[sectionKey] && Array.isArray(canadaFeedsConfig[sectionKey])) {
        canadaFeedsConfig[sectionKey].forEach(feed => {
          if (feed.apiEndpoint && feed.title && feed.showOnHomePage !== false) {
            const schemaMatch = feed.apiEndpoint.match(/schema=([^&]+)/);
            const tableMatch = feed.apiEndpoint.match(/table=([^&]+)/);
            
            if (schemaMatch && tableMatch) {
              const schema = decodeURIComponent(schemaMatch[1]);
              const table = decodeURIComponent(tableMatch[1]);
              
              let feedRoute = `/feed/${schema}/${table}`;
              
              const filterMatch = feed.apiEndpoint.match(/filter_column=([^&]+)&filter_string=([^&]+)/);
              if (filterMatch) {
                const filterColumn = decodeURIComponent(filterMatch[1]);
                const filterString = decodeURIComponent(filterMatch[2]);
                feedRoute += `?filter_column=${filterColumn}&filter_string=${encodeURIComponent(filterString)}`;
              }
              
              results.push({
                title: feed.title,
                description: `View all items in the ${feed.title.toLowerCase()} feed`,
                route: feedRoute,
                icon: 'List',
                type: 'feed',
                priority: 4
              });
            }
          }
        });
      }

      // console.log('Found section match:', sectionInfo.title);
    }
  });

  // 4. Search individual feeds by name
  Object.entries(canadaFeedsConfig).forEach(([sectionKey, sectionFeeds]) => {
    if (!Array.isArray(sectionFeeds)) return;
    
    sectionFeeds.forEach(feed => {
      if (!feed.title || !feed.apiEndpoint) return;
      
      const feedTitle = feed.title.toLowerCase();
      const feedName = (feed.feedName || '').toLowerCase();
      
      if (feedTitle.includes(normalizedQuery) || 
          feedName.includes(normalizedQuery) ||
          normalizedQuery.includes(feedTitle)) {
        
        const schemaMatch = feed.apiEndpoint.match(/schema=([^&]+)/);
        const tableMatch = feed.apiEndpoint.match(/table=([^&]+)/);
        
        if (schemaMatch && tableMatch) {
          const schema = decodeURIComponent(schemaMatch[1]);
          const table = decodeURIComponent(tableMatch[1]);
          
          let feedRoute = `/feed/${schema}/${table}`;
          
          const filterMatch = feed.apiEndpoint.match(/filter_column=([^&]+)&filter_string=([^&]+)/);
          if (filterMatch) {
            const filterColumn = decodeURIComponent(filterMatch[1]);
            const filterString = decodeURIComponent(filterMatch[2]);
            feedRoute += `?filter_column=${filterColumn}&filter_string=${encodeURIComponent(filterString)}`;
          }
          
          results.push({
            title: feed.title,
            description: `View all items in the ${feed.title.toLowerCase()} feed`,
            route: feedRoute,
            icon: 'List',
            type: 'feed',
            priority: 3
          });
          
          // console.log('Found feed match:', feed.title);
        }
      }
    });
  });

  // Remove duplicates and sort by priority
  const uniqueResults = results.filter((item, index, self) => 
    index === self.findIndex(t => t.route === item.route)
  );

  const finalResults = uniqueResults
    .sort((a, b) => a.priority - b.priority)
    .slice(0, limit);

  // console.log('Final Canada results:', finalResults.length);
  // console.log('=== END CANADA SEARCH ===');
  
  return finalResults;
};

// Enhanced content type detection for Canada
export const enrichCanadaSearchResult = (item) => {
  try {
    const title = (item.title || '').toLowerCase();
    const description = (item.description || item.snippet || '').toLowerCase();
    const source = (item.source || '').toLowerCase();
    const collectionName = (item.collection_name || '').toLowerCase();

    // Check for Canadian department patterns
    for (const [deptKey, deptConfig] of Object.entries(canadaDepartmentConfig)) {
      if (!deptConfig.departmentInfo) continue;
      
      const deptName = deptConfig.departmentInfo.name.toLowerCase();
      const deptKeywords = [
        deptName,
        deptConfig.departmentInfo.name.replace(/canada /i, ''),
        deptConfig.departmentInfo.name.replace(/ of canada/i, ''),
        deptKey
      ];

      if (deptKeywords.some(keyword => 
        title.includes(keyword) || 
        source.includes(keyword) || 
        collectionName.includes(keyword)
      )) {
        return {
          type: 'department',
          departmentKey: deptKey,
          department: deptConfig.departmentInfo,
          route: deptConfig.departmentInfo.route
        };
      }
    }

    // Check for provincial patterns
    for (const [provinceKey, provinceData] of Object.entries(provincialInfo)) {
      const provinceName = provinceData.fullName.toLowerCase();
      
      if (title.includes(provinceName) || 
          source.includes(provinceName) || 
          collectionName.includes(provinceName)) {
        return {
          type: 'province',
          provinceKey: provinceKey,
          province: provinceData,
          route: `/canada/${provinceKey}`
        };
      }
    }

    // Check for section patterns
    const sectionKeywords = {
      'legislative': ['parliament', 'bill', 'senate', 'house of commons', 'committee'],
      'executive': ['prime minister', 'government', 'minister', 'agency'],
      'judicial': ['supreme court', 'court', 'judicial', 'justice'],
      'environment': ['environment', 'climate', 'natural resources'],
      'economy': ['revenue', 'economic', 'finance', 'treasury']
    };

    for (const [sectionKey, keywords] of Object.entries(sectionKeywords)) {
      if (keywords.some(keyword => title.includes(keyword) || description.includes(keyword))) {
        return {
          type: 'section',
          sectionKey: sectionKey,
          route: `/canada/${sectionKey}`
        };
      }
    }

    return { type: 'document' };
  } catch (error) {
    console.warn('Error in enrichCanadaSearchResult:', error);
    return { type: 'document' };
  }
};

// Export functions for use in components
export const getCanadaNavigationSuggestions = (query, limit = 6) => {
  if (!query || !query.trim()) return [];
  
  const suggestions = searchCanadaRelevantPages(query, limit);
  
  return suggestions.map(suggestion => ({
    title: suggestion.title,
    description: suggestion.description,
    route: suggestion.route,
    type: suggestion.type,
    icon: getIconForType(suggestion.type),
    priority: suggestion.priority || 5
  }));
};

const getIconForType = (type) => {
  const iconMap = {
    'department': 'Building',
    'agency': 'Building',
    'province': 'Map',
    'section': 'Globe',
    'feed': 'List',
    'document': 'FileText'
  };
  
  return iconMap[type] || 'InfoCircle';
};

export default {
  searchCanadaRelevantPages,
  enrichCanadaSearchResult,
  getCanadaNavigationSuggestions
};